import React, { useRef, useEffect, useState } from "react";
import { Box, Typography, TypographyProps } from "@mui/material";
import { makeStyles } from "@mui/styles";
import { Theme } from "@mui/material/styles";

const useStyles = makeStyles<Theme, OverflowTypographyProps & { showFade: boolean }>(
  (theme) => ({
    root: {
      display: "flex",
      alignItems: "center",
      width: (props) => props.width,
      height: (props) => props.height,
      overflow: "hidden",
      position: "relative",
      backgroundColor: (props) => props.fadeColor,
      "&:before": {
        display: (props) => (props.showFade ? "initial" : "none"),
        zIndex: 1,
        content: '""',
        position: "absolute",
        top: 0,
        bottom: 0,
        right: 0,
        width: "50px",
        opacity: (props) => (props.alignment === "left" ? 1 : 0),
        background: (props) =>
          `linear-gradient(to left, ${props.fadeColor}, transparent)`,
        transition: "opacity 500ms ease-in-out",
      },
      "&:after": {
        display: (props) => (props.showFade ? "initial" : "none"),
        zIndex: 1,
        content: '""',
        position: "absolute",
        top: 0,
        bottom: 0,
        left: 0,
        width: "50px",
        opacity: (props) => (props.alignment === "left" ? 0 : 1),
        background: (props) =>
          `linear-gradient(to right, ${props.fadeColor}, transparent)`,
        transition: "opacity 500ms ease-in-out",
      },
      "&:hover": {
        "&:after": {
          opacity: (props) => (props.alignment === "left" ? 1 : 0),
        },
        "&:before": {
          opacity: (props) => (props.alignment === "left" ? 0 : 1),
        },
      },
    },
    typography: {
      position: "absolute",
      whiteSpace: "nowrap",
      transitionProperty: "left, right, opacity",
      transitionDuration: (props) => props.transitionDuration,
      transitionTimingFunction: "ease-in-out",
    },
  })
);

interface OverflowTypographyProps extends TypographyProps {
  /** Default "100%" */
  width?: string;
  /** Default "1.2em" */
  height?: string;
  /** Default "left" */
  alignment?: "left" | "right";
  /** Default "500ms" */
  transitionDuration?: string;
  /** Default "#FFF" */
  fadeColor?: string;
  /** The underlying component to render (e.g., span, div, etc.). */
  component?: React.ElementType;
}

export const OverflowTypography: React.FC<OverflowTypographyProps> = (props) => {
  const {
    children,
    alignment = "left",
    width = "100%",
    height = "1.2em",
    transitionDuration = "500ms",
    fadeColor = "#FFF",
  } = props;
  const typographyProps = {
    ...props,
    width: undefined,
    height: undefined,
    alignment: undefined,
    fadeColor: undefined,
  };

  const parentRef = useRef<HTMLDivElement>(null);
  const typographyRef = useRef<HTMLDivElement>(null);
  const [isHovered, setIsHovered] = useState(false);
  const [offset, setOffset] = useState(0);
  const [showFade, setShowFade] = useState(false);

  const classes = useStyles({
    ...props,
    alignment,
    width,
    height,
    transitionDuration,
    fadeColor,
    showFade,
  });

  const updateOffset = () => {
    if (typographyRef.current && parentRef.current) {
      const boundingRect = typographyRef.current.getBoundingClientRect();
      const boundingRectParent = parentRef.current.getBoundingClientRect();
      setOffset(boundingRect.width - boundingRectParent.width);
      setShowFade(boundingRect.width > boundingRectParent.width);
    }
  };

  useEffect(() => {
    updateOffset();
  }, [children]);

  useEffect(() => {
    const handleResize = () => updateOffset();
    window.addEventListener("resize", handleResize);

    const currentParentRef = parentRef.current;
    const resizeObserver = new ResizeObserver(() => updateOffset());
    if (currentParentRef) {
      resizeObserver.observe(currentParentRef);
    }

    return () => {
      window.removeEventListener("resize", handleResize);
      if (currentParentRef) {
        resizeObserver.unobserve(currentParentRef);
      }
    };
  }, []);

  const handleMouseEnter = () => setIsHovered(true);
  const handleMouseLeave = () => setIsHovered(false);

  return (
    <Box
      className={classes.root}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      ref={parentRef}>
      <Typography
        {...typographyProps}
        className={classes.typography}
        ref={typographyRef}
        style={{
          ...(alignment === "left" && { left: isHovered ? `-${offset}px` : "0px" }),
          ...(alignment === "right" && { right: isHovered ? `-${offset}px` : "0px" }),
        }}>
        {children}
      </Typography>
    </Box>
  );
};
